import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useEffect, useState } from "react";
import { Instance, Store } from "../../types/whatsapp";
import { Autocomplete } from "../Autocomplete";
import { useFormik } from "formik";
//import { useStyles } from "./styles";
import * as Yup from "yup";

interface ConfigInstanceDialogProps {
  open: boolean;
  loading?: boolean;
  loadingSubmit?: boolean;
  instance: Instance;
  stores?: Store[];
  isNewInstance?: boolean | null;
  hasBeenPresentedWarning?: boolean | null;
  onClose?: () => void;
  onSave: (instance: FormikInstance) => void;
  onDelete?: () => void;
}

export type FormikInstance = {
  name: string;
  id_loja: number | null;
  instance_type: number;
  own_number?: string | null;
  automatically_activate_contacts: number;
  automatically_save_groups: number;
  unanswered_minutes?: number | null;
  opening_message?: string | null;
  message_dont_understand?: string | null;
  final_message_chat?: string | null;
  automatic_answer?: string | null;
};

const validationSchema = Yup.object().shape({
  instance_type: Yup.number().min(1).max(2),
  id_loja: Yup.number().nullable().required("Informe a loja"),
  name: Yup.string().required("Informe o nome"),
  own_number: Yup.string().required("Informe o número"),
  accept_agreement: Yup.boolean().oneOf(
    [true],
    "Você deve aceitar o acordo para criar uma nova instância"
  ),
  automatically_activate_contacts: Yup.number(),
  automatically_save_groups: Yup.number(),
  opening_message: Yup.string().when("instance_type", {
    is: 2,
    then: (schema) => schema.required("Informe a mensagem de inicial"),
  }),
  message_dont_understand: Yup.string().when("instance_type", {
    is: 2,
    then: (schema) => schema.required("Informe a mensagem não entendi"),
  }),
  automatic_answer: Yup.string().when("instance_type", {
    is: 2,
    then: (schema) => schema.required("Informe a mensagem automática"),
  }),
  final_message_chat: Yup.string().when("instance_type", {
    is: 2,
    then: (schema) => schema.required("Informe a mensagem final"),
  }),
  unanswered_minutes: Yup.number().when("instance_type", {
    is: 2,
    then: (schema) =>
      schema
        .min(1, "O valor minimo é 1")
        .required("Informe os minutos sem resposta"),
  }),
});

export function ConfigInstanceDialog({
  open,
  loadingSubmit,
  instance,
  stores,
  isNewInstance,
  onClose,
  onSave,
  hasBeenPresentedWarning,
}: ConfigInstanceDialogProps) {
  //const styles = useStyles();
  const [storeSelected, setStoreSelected] = useState<Store>();

  const handleSubmit = (values: FormikInstance) => {
    onSave(values);
  };

  const formik = useFormik({
    initialValues: {
      accept_agreement: !isNewInstance ? true : false,
      name: instance?.nome || "",
      id_loja: instance?.id_loja || null,
      instance_type: instance?.tipo_instancia || 1,
      own_number: instance?.own_number || "",
      automatically_activate_contacts:
        instance?.automatically_activate_contacts || 1,
      automatically_save_groups: instance?.automatically_save_groups || 1,
      opening_message: instance?.mensagem_inicial || " ",
      message_dont_understand: instance?.mensagem_nao_entendi || " ",
      final_message_chat: instance?.mensagem_final_chat || " ",
      automatic_answer: instance?.resposta_automatica || "",
      unanswered_minutes: instance?.minutos_sem_resposta || 1440,
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: handleSubmit,
  });

  const handleClose = (reset = false) => {
    onClose?.();
    if (reset) {
      formik.resetForm();
      setStoreSelected(undefined);
    }
  };

  useEffect(() => {
    if (!stores && !instance) setStoreSelected(undefined);
    let findStore = stores?.find(
      (store) => store.id_loja === formik.values.id_loja
    );
    setStoreSelected(findStore);
  }, [formik.values.id_loja, instance, stores]);

  return (
    <Dialog open={open} maxWidth={"md"} fullWidth onClose={() => handleClose()}>
      <Box p="32px" component="form" onSubmit={formik.handleSubmit}>
        {isNewInstance && (
          <DialogTitle sx={{ p: 0 }} fontSize="1.5rem">
            Adicione novo número
            <Typography fontWeight={500} color="#616161">
              Ao adicionar um número, você poderá fazer envios para seus
              contatos e grupos de WhatsApp.
            </Typography>
          </DialogTitle>
        )}
        <DialogContent sx={{ p: 0 }}>
          {/* <Box borderBottom={"1px solid #D3D5DB"}>
              <Button
                className={styles.btnInstaceType}
                onClick={() => formik.setFieldValue("instance_type", 1)}
                sx={{
                  ...(formik.values.instance_type === 1 && {
                    color: "#0C20B5 !important",
                    borderBottom: "solid 2px #0C20B5 !important",
                  }),
                }}
              >
                Disparo
              </Button>
              <Button
                className={styles.btnInstaceType}
                onClick={() => formik.setFieldValue("instance_type", 2)}
                sx={{
                  ...(formik.values.instance_type === 2 && {
                    color: "#0C20B5 !important",
                    borderBottom: "solid 2px #0C20B5 !important",
                  }),
                }}
              >
                Chatbot
              </Button>
            </Box> */}
          <Box display="flex" flexDirection="column" p="32px 0" gap={3}>
            <TextField
              fullWidth
              label="Nome"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange("name")}
              error={Boolean(formik.errors.name)}
              helperText={
                formik.errors.name || "Dê um nome para identificar este número"
              }
            />

            <Autocomplete
              size="medium"
              label="Selecione a loja"
              clearOnBlur={false}
              options={stores}
              value={storeSelected}
              getOptionLabel={(option) => option.nome_loja}
              onChange={(_, option) => {
                formik.setFieldValue("id_loja", option ? option.id_loja : null);
              }}
              error={Boolean(formik.errors.id_loja)}
              helperText={
                formik.errors.id_loja ||
                "Escolha a qual loja este número pertence"
              }
            />
            <TextField
              label="Celular da sua loja"
              name="own_number"
              fullWidth
              value={formik.values.own_number}
              onChange={formik.handleChange("own_number")}
              error={Boolean(formik.errors.own_number)}
              helperText={formik.errors.own_number || null}
            />

            <Box display="flex" flexDirection="row" gap={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="automatically_activate_contacts"
                    onChange={({ target: { checked, name } }) =>
                      formik.setFieldValue(name, checked ? 1 : 0)
                    }
                    checked={Boolean(
                      formik.values.automatically_activate_contacts
                    )}
                    sx={{ p: 0, mr: 1 }}
                  />
                }
                label="Salvar contatos"
                sx={{ m: 0, cursor: "pointer" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="automatically_save_groups"
                    onChange={({ target: { checked, name } }) =>
                      formik.setFieldValue(name, checked ? 1 : 0)
                    }
                    checked={Boolean(formik.values.automatically_save_groups)}
                    sx={{ p: 0, mr: 1 }}
                  />
                }
                label="Salvar grupos"
                sx={{ m: 0, cursor: "pointer" }}
              />
            </Box>
            {!hasBeenPresentedWarning && (
              <>
                <Box>
                  <Alert
                    variant="filled"
                    severity="warning"
                    sx={{ backgroundColor: "#F7B304", color: "#0C2555" }}
                  >
                    Atencão! ao criar uma nova instância, todos os contatos
                    serão ativados. Se você deseja manter o status de ativação
                    ou inativação dos contatos, por favor, reconecte o WhatsApp
                    a uma instância já existente.
                  </Alert>
                </Box>
                <Box>
                  <Alert
                    variant="filled"
                    severity="info"
                    sx={{ backgroundColor: "#1890FF", color: "#FFFFFF" }}
                  >
                    Ao conectar um número, somente os contatos com o mesmo DDD
                    do número conectado serão ativados automaticamente. Mas você
                    pode ativar os contatos inativos clicando em “Filtrar” {">"}{" "}
                    “Inativos”, depois basta selecionar todos e ativar.
                  </Alert>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="accept_agreement"
                        onChange={({ target: { checked, name } }) =>
                          formik.setFieldValue(name, checked ? 1 : 0)
                        }
                        checked={Boolean(formik.values.accept_agreement)}
                        sx={{ p: 0, mr: 1 }}
                      />
                    }
                    label="Estou de acordo e desejo criar uma nova instância mesmo assim"
                    sx={{ m: 0, cursor: "pointer" }}
                  />
                  <Box sx={{ mt: 1 }}>
                    {formik.errors.accept_agreement && (
                      <Typography variant="button" color="error">
                        Este campo é obrigatório para criar uma nova instância.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Box>
          {formik.values.instance_type === 2 && (
            <Box display="flex" flexDirection="column" p="16px 0" gap={3}>
              <TextField
                multiline
                rows={7}
                label="Mensagem Inicial"
                name="opening_message"
                fullWidth
                value={formik.values.opening_message}
                onChange={formik.handleChange("opening_message")}
                error={Boolean(formik.errors.opening_message)}
                helperText={formik.errors.opening_message || null}
              />

              <TextField
                multiline
                rows={7}
                label="Mensagem não entendi"
                name="message_dont_understand"
                fullWidth
                value={formik.values.message_dont_understand}
                onChange={formik.handleChange("message_dont_understand")}
                error={Boolean(formik.errors.message_dont_understand)}
                helperText={formik.errors.message_dont_understand || null}
              />

              <TextField
                multiline
                rows={7}
                label="Resposta Automática"
                name="automatic_answer"
                fullWidth
                value={formik.values.automatic_answer}
                onChange={formik.handleChange("automatic_answer")}
                error={Boolean(formik.errors.automatic_answer)}
                helperText={formik.errors.automatic_answer || null}
              />

              <TextField
                multiline
                rows={7}
                label="Mensagem de finalização de chat"
                name="final_message_chat"
                fullWidth
                value={formik.values.final_message_chat}
                onChange={formik.handleChange("final_message_chat")}
                error={Boolean(formik.errors.final_message_chat)}
                helperText={formik.errors.final_message_chat || null}
              />

              <TextField
                sx={{ width: "16rem" }}
                label="Minutos sem resposta automática"
                name="unanswered_minutes"
                fullWidth
                value={formik.values.unanswered_minutes}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (Number(e.target.value) >= 1 || e.target.value === "") {
                    formik.setFieldValue("unanswered_minutes", e.target.value);
                  }
                }}
                error={Boolean(formik.errors.unanswered_minutes)}
                helperText={formik.errors.unanswered_minutes || null}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{ p: 0, display: "flex", justifyContent: "space-between" }}
        >
          <Button color="inherit" onClick={() => handleClose(true)}>
            Cancelar
          </Button>
          <Button variant="contained" type="submit" disabled={loadingSubmit}>
            {isNewInstance ? "Continuar" : "Salvar"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
